import { useEffect } from "react";
import { isProductionHost } from "../utils/isProductionHost";

// Many pages embed their own <script type="application/ld+json"> schema
// directly via react-helmet-async, outside of <SEO/>. Gating every one of
// those individually is easy to miss on the next new page, so instead this
// runs once, globally, and strips any structured-data script from <head> on
// every host except production — staging.einsured.ca, preview/staging2
// hosts, etc. (127.0.0.1/localhost are excluded via isProductionHost since
// that's what scripts/prerender.mjs uses to bake the static HTML shipped to
// every environment — see that file's comment for why.)
//
// The <meta name="robots"> tag is pinned directly here too, rather than
// trusting react-helmet-async's own noindex logic in App.jsx/SEO.jsx to take
// effect client-side: verified live on staging that Helmet does NOT reliably
// overwrite that tag's `content` once it's already present from the
// prerendered static HTML (the schema removal above works because it's a
// raw DOM removal, not a Helmet reconciliation — same approach applied here
// for the same reliability). This runs on every host except production.
export default function NonProductionSeoGuard() {
  useEffect(() => {
    if (isProductionHost()) return undefined;

    const enforce = () => {
      document
        .querySelectorAll('head script[type="application/ld+json"]')
        .forEach((el) => el.remove());

      let robots = document.querySelector('head meta[name="robots"]');
      if (!robots) {
        robots = document.createElement("meta");
        robots.setAttribute("name", "robots");
        document.head.appendChild(robots);
      }
      if (robots.getAttribute("content") !== "noindex, nofollow") {
        robots.setAttribute("content", "noindex, nofollow");
      }
    };

    enforce();

    const observer = new MutationObserver(enforce);
    observer.observe(document.head, { childList: true, subtree: true, attributes: true });

    return () => observer.disconnect();
  }, []);

  return null;
}
